import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ArrowRight, Shield, CheckCircle, XCircle, AlertTriangle, Clock, FileText, MessageSquare, RefreshCw, Scale } from 'lucide-react';
import SEO from '../SEO';
import '../SeoPages.css';

function AppealStrikesGuide() {
  return (
    <>
      <SEO
        title="How to Appeal YouTube & TikTok Strikes: Complete Guide (2026)"
        description="Step-by-step guide to appealing content strikes on YouTube and TikTok. Learn how to write effective appeals, understand the process, and get your content restored."
        keywords="appeal YouTube strike, appeal TikTok strike, YouTube community guidelines appeal, TikTok appeal process, remove YouTube strike, content strike appeal, YouTube copyright appeal"
        url="/guides/appeal-strikes"
      />
      <div className="seo-page">
        <div className="seo-container">
          {/* Header */}
          <header className="seo-header">
            <Link to="/" className="seo-back-link">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="seo-brand">
              <div className="seo-logo-circle">
                <Sparkles className="seo-sparkles-icon" />
              </div>
              <span className="seo-brand-name">MyCleanStudio</span>
            </Link>
          </header>

          {/* Hero */}
          <div className="seo-hero">
            <h1>How to Appeal YouTube & TikTok Strikes</h1>
            <p className="seo-hero-subtitle">
              Got a strike on your content? Learn exactly how to appeal and get your content restored.
            </p>
          </div>

          {/* Content */}
          <main className="seo-content">
            <div className="seo-toc">
              <h3>In This Guide</h3>
              <ul>
                <li><a href="#understanding">Understanding Strikes</a></li>
                <li><a href="#youtube-appeal">YouTube Appeal Process</a></li>
                <li><a href="#tiktok-appeal">TikTok Appeal Process</a></li>
                <li><a href="#writing-appeals">Writing Effective Appeals</a></li>
                <li><a href="#after-appeal">After You Appeal</a></li>
                <li><a href="#prevention">Preventing Future Strikes</a></li>
              </ul>
            </div>

            <section id="understanding">
              <h2>Understanding Strikes</h2>
              <p>
                Content strikes are penalties platforms issue when your content violates their guidelines. Not all removals are the same - understanding the type of strike helps you craft the right appeal.
              </p>

              <h3>Types of Strikes</h3>
              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Community Guidelines Strike</h4>
                  <p>Your content violated platform rules around violence, hate speech, harassment, or other prohibited content.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <FileText size={20} />
                  </div>
                  <h4>Copyright Strike</h4>
                  <p>A rights holder claimed your content uses their copyrighted material without permission.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon purple">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Content ID Claim</h4>
                  <p>YouTube only - automated detection of copyrighted music or video. Not a strike, but affects monetization.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon blue">
                    <Shield size={20} />
                  </div>
                  <h4>Age Restriction</h4>
                  <p>Content limited to adult viewers only. Not a strike, but limits reach and monetization.</p>
                </div>
              </div>

              <div className="seo-warning-box">
                <p>
                  <strong>Strike Consequences:</strong> Three community guidelines strikes within 90 days results in channel termination on YouTube. TikTok may ban accounts after repeated violations.
                </p>
              </div>
            </section>

            <section id="youtube-appeal">
              <h2>YouTube Appeal Process</h2>
              <p>
                YouTube provides a formal appeal process for strikes. Here's exactly how to navigate it:
              </p>

              <h3>Step 1: Find Your Strike</h3>
              <ul>
                <li>Go to <strong>YouTube Studio</strong></li>
                <li>Click <strong>Content</strong> in the left menu</li>
                <li>Look for videos with restriction icons</li>
                <li>Or go to <strong>Channel</strong> → <strong>Status and features</strong> to see account-level strikes</li>
              </ul>

              <h3>Step 2: Review the Violation</h3>
              <ul>
                <li>Click on the strike to see which guideline was violated</li>
                <li>Watch your video again with fresh eyes</li>
                <li>Read YouTube's specific policy for that violation type</li>
                <li>Honestly assess if the strike might be valid</li>
              </ul>

              <h3>Step 3: Submit Your Appeal</h3>
              <ul>
                <li>Click the <strong>Appeal</strong> button on the strike notification</li>
                <li>Select the reason for your appeal</li>
                <li>Write a clear, respectful explanation (see tips below)</li>
                <li>Submit and wait for review</li>
              </ul>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Clock size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>YouTube Review Timeline</h4>
                    <p>First appeals are typically reviewed within 2-7 business days. If rejected, you can request a second review which may take longer.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <RefreshCw size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Second Appeal Option</h4>
                    <p>If your first appeal is rejected, YouTube allows one more appeal. This is reviewed by a different team member for a fresh perspective.</p>
                  </div>
                </div>
              </div>

              <h3>Copyright Strike Appeals</h3>
              <p>Copyright strikes have a different process:</p>
              <ul>
                <li><strong>Counter-notification</strong> - If you believe it's fair use or you have rights</li>
                <li><strong>Retraction request</strong> - Contact the claimant directly to request removal</li>
                <li><strong>Wait it out</strong> - Copyright strikes expire after 90 days if no further action</li>
              </ul>

              <div className="seo-warning-box">
                <p>
                  <strong>Legal Notice:</strong> Copyright counter-notifications are legal documents. Filing a false counter-notification can have legal consequences. Only file if you genuinely believe the claim is wrong.
                </p>
              </div>
            </section>

            <section id="tiktok-appeal">
              <h2>TikTok Appeal Process</h2>
              <p>
                TikTok's appeal process is simpler but also less transparent. Here's how to navigate it:
              </p>

              <h3>Step 1: Find the Violation Notice</h3>
              <ul>
                <li>Open TikTok and go to your <strong>Inbox</strong></li>
                <li>Look for notifications from TikTok about removed content</li>
                <li>Or go to <strong>Settings</strong> → <strong>Account</strong> → <strong>Account Status</strong></li>
              </ul>

              <h3>Step 2: Submit an Appeal</h3>
              <ul>
                <li>Tap on the violation notification</li>
                <li>Select <strong>Appeal</strong> or <strong>Submit an appeal</strong></li>
                <li>Provide your explanation (brief - TikTok has character limits)</li>
                <li>Submit and wait</li>
              </ul>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Clock size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>TikTok Review Timeline</h4>
                    <p>TikTok appeals are usually reviewed within 24-48 hours, though complex cases may take longer. You'll receive an in-app notification with the result.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <MessageSquare size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Limited Appeal Options</h4>
                    <p>Unlike YouTube, TikTok typically only allows one appeal per violation. Make your first appeal count.</p>
                  </div>
                </div>
              </div>

              <h3>Account Ban Appeals</h3>
              <p>If your entire TikTok account is banned:</p>
              <ul>
                <li>Open TikTok - you'll see a banner about the ban</li>
                <li>Tap <strong>Appeal</strong> on the ban notice</li>
                <li>Explain why you believe the ban was a mistake</li>
                <li>Provide any relevant context or evidence</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  <strong>Shadowban vs. Ban:</strong> If your videos suddenly get no views but you can still post, you might be shadowbanned - not officially banned. Shadowbans can't be appealed directly. See our <Link to="/guides/tiktok-guidelines">TikTok Guidelines Guide</Link> for recovery tips.
                </p>
              </div>
            </section>

            <section id="writing-appeals">
              <h2>Writing Effective Appeals</h2>
              <p>
                Your appeal is often reviewed quickly by moderators handling hundreds of cases. Make yours stand out with a clear, professional approach.
              </p>

              <h3>Do's and Don'ts</h3>
              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon green">
                    <CheckCircle size={20} />
                  </div>
                  <h4>Be Specific</h4>
                  <p>Reference the exact timestamp or content in question. Explain specifically why it doesn't violate guidelines.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon green">
                    <CheckCircle size={20} />
                  </div>
                  <h4>Stay Professional</h4>
                  <p>Be polite and respectful. Angry appeals are less likely to succeed. Treat it like a business communication.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon green">
                    <CheckCircle size={20} />
                  </div>
                  <h4>Provide Context</h4>
                  <p>Explain educational intent, satire, or news reporting context. Help reviewers understand your purpose.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Don't Be Vague</h4>
                  <p>Avoid "I didn't do anything wrong" without explanation. Generic appeals are easily rejected.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Don't Threaten</h4>
                  <p>Threatening legal action or bad publicity rarely helps. It may make reviewers less sympathetic.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Don't Lie</h4>
                  <p>If your content did violate guidelines, admit the mistake. Dishonest appeals can result in harsher penalties.</p>
                </div>
              </div>

              <h3>Appeal Template Structure</h3>
              <ol>
                <li><strong>Acknowledge</strong> - Show you understand which guideline they cited</li>
                <li><strong>Explain</strong> - Describe what your content actually shows/says</li>
                <li><strong>Context</strong> - Provide any important context (educational, news, satire)</li>
                <li><strong>Request</strong> - Politely ask for reconsideration</li>
              </ol>

              <div className="seo-highlight-box">
                <p>
                  <strong>Example:</strong> "My video was flagged for violent content, but the footage shows a video game with no real-world violence. The game is rated T for Teen and is commonly streamed on this platform. I believe this was incorrectly flagged and respectfully request a review."
                </p>
              </div>
            </section>

            <section id="after-appeal">
              <h2>After You Appeal</h2>
              <p>
                Once you've submitted your appeal, here's what to expect:
              </p>

              <h3>If Your Appeal Succeeds</h3>
              <ul>
                <li>Your content will be restored</li>
                <li>The strike will be removed from your account</li>
                <li>Any lost monetization may not be recovered</li>
                <li>Consider downloading a backup of important content</li>
              </ul>

              <h3>If Your Appeal Fails</h3>
              <ul>
                <li><strong>YouTube:</strong> You may have one more appeal available</li>
                <li><strong>TikTok:</strong> Options are limited - focus on not getting more strikes</li>
                <li>Accept the decision and learn from it</li>
                <li>The strike will expire eventually (90 days for YouTube)</li>
                <li>Focus on creating compliant content going forward</li>
              </ul>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Scale size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>When to Seek Legal Help</h4>
                    <p>For copyright disputes involving significant revenue or if you believe your legal rights are being violated, consulting a media lawyer may be worthwhile. Most community guideline strikes don't require legal intervention.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="prevention">
              <h2>Preventing Future Strikes</h2>
              <p>
                The best appeal is the one you never have to file. Here's how to protect your content:
              </p>

              <ul>
                <li><strong>Know the rules</strong> - Read platform guidelines thoroughly and check for updates</li>
                <li><strong>Check before posting</strong> - Review your content with fresh eyes before uploading</li>
                <li><strong>Use royalty-free music</strong> - Avoid copyright issues entirely</li>
                <li><strong>Add context</strong> - Use titles and descriptions to clarify educational or satirical intent</li>
                <li><strong>Watch similar content</strong> - See what other creators get away with (and what gets them strikes)</li>
                <li><strong>Keep backups</strong> - Always save copies of your content locally</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  MyCleanStudio scans your videos for potential guideline violations and copyright issues before you upload, helping you catch problems before they become strikes.
                </p>
              </div>
            </section>

            {/* CTA */}
            <div className="seo-cta">
              <h2>Prevent Strikes Before They Happen</h2>
              <p>Scan your videos for potential violations before uploading.</p>
              <Link to="/dashboard/new-check" className="seo-cta-button">
                Check Your Content
                <ArrowRight size={18} />
              </Link>
            </div>
          </main>

          {/* Footer */}
          <footer className="seo-footer">
            <div className="seo-footer-links">
              <Link to="/guides/youtube-guidelines">YouTube Guidelines</Link>
              <Link to="/guides/tiktok-guidelines">TikTok Guidelines</Link>
              <Link to="/guides/youtube-demonetization">Demonetization Guide</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} MyCleanStudio. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
}

export default AppealStrikesGuide;
